import React, { useMemo } from "react";
import type { FormListFieldData } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { Form, Input, Select, Button } from "../../../../components/shared";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import EventPropertyField from "../controls/EventPropertyField";
import { fieldOperatorOptions } from "../constants";
import { fieldsForEvent, toGroupedSelectOptions, withoutCampaignStart } from "../eventCatalog";
import { useDynamicBonusEvents } from "../../../../queries/dynamicBonuses/useDynamicBonusEvents";
import type { CatalogEventField, EventCategoryGroup } from "../types";
import styles from "../stylesDynamicBonuses.module.css";

type FieldCheckRowProps = {
  /** Absolute path to this rule's `field_checks` list, e.g. `["membership_rules", 0, "field_checks"]`. */
  namePath: (string | number)[];
  field: FormListFieldData;
  /** Catalog `filterable_fields` for the owning rule's `event_key`. */
  fields: CatalogEventField[];
  onRemove: () => void;
};

/**
 * Single field-check row. Watches its own `operator` so the Value input can
 * hide for `exists` — mirrors `describeCheck`'s read-only rendering, which
 * never shows a value for that operator.
 */
const FieldCheckRow: React.FC<FieldCheckRowProps> = ({
  namePath,
  field,
  fields,
  onRemove,
}) => {
  const { key, name, ...restField } = field;
  const operator = Form.useWatch([...namePath, name, "operator"]) as string | undefined;

  return (
    <div key={key} className={styles.conditionRow}>
      <div className={styles.conditionRowFields}>
        <Form.Item
          {...restField}
          name={[name, "field"]}
          rules={[{ required: true, message: "Field required" }]}
          style={{ flex: "1 1 200px", marginBottom: 0 }}
        >
          <EventPropertyField fields={fields} placeholder="Field" />
        </Form.Item>

        <Form.Item
          {...restField}
          name={[name, "operator"]}
          rules={[{ required: true, message: "Operator required" }]}
          style={{ flex: "0 0 180px", marginBottom: 0 }}
        >
          <Select placeholder={AppStrings.PAM_DYNAMIC_BONUS_PLACEHOLDER_OPERATOR} options={fieldOperatorOptions()} />
        </Form.Item>

        {operator !== "exists" && (
          <Form.Item
            {...restField}
            name={[name, "value"]}
            rules={[{ required: true, message: "Value required" }]}
            style={{ flex: "1 1 150px", marginBottom: 0 }}
          >
            <Input placeholder={AppStrings.PAM_DYNAMIC_BONUS_PLACEHOLDER_VALUE} />
          </Form.Item>
        )}
      </div>

      <Button
        intent="danger"
        variant="ghost"
        iconLeft={<DeleteOutlined />}
        onClick={onRemove}
        aria-label="Remove field check"
        style={{ flexShrink: 0 }}
      />
    </div>
  );
};

type RuleCardProps = {
  /** Absolute path to the rules `Form.List`, e.g. `["membership_rules"]`. */
  namePath: (string | number)[];
  field: FormListFieldData;
  index: number;
  eventGroups: EventCategoryGroup[];
  eventSelectOptions: ReturnType<typeof toGroupedSelectOptions>;
  eventsLoading: boolean;
  eventsError: boolean;
  onRemove: () => void;
};

/**
 * One membership rule — `event_key` plus a nested `Form.List` of field
 * checks. `rule_id` round-trips as a hidden field so existing rules keep
 * their identity on save (the caller merges back the original per-rule
 * `ttl_days`, which isn't edited here); only `event_key` and
 * `field_checks` are editable per the current scope.
 */
const RuleCard: React.FC<RuleCardProps> = ({
  namePath,
  field,
  index,
  eventGroups,
  eventSelectOptions,
  eventsLoading,
  eventsError,
  onRemove,
}) => {
  const { key, name, ...restField } = field;
  const eventKey = Form.useWatch([...namePath, name, "event_key"]) as string | undefined;
  const ruleId = Form.useWatch([...namePath, name, "rule_id"]) as string | undefined;
  const fieldChecksPath = [...namePath, name, "field_checks"];
  const fields = fieldsForEvent(eventGroups, eventKey ?? "");
  const isLegacyUnmapped = Boolean(ruleId) && !eventKey;

  return (
    <div key={key} className={styles.stepCard} data-testid={`segment-rule-card-${index}`}>
      <div className={styles.stepCardHeader}>
        <p className={styles.stepCardTitle}>Rule {index + 1}</p>
        <Button
          intent="danger"
          variant="ghost"
          iconLeft={<DeleteOutlined />}
          onClick={onRemove}
          aria-label={`Remove Rule ${index + 1}`}
        />
      </div>
      <div className={styles.stepCardBody}>
        <Form.Item {...restField} name={[name, "rule_id"]} hidden>
          <Input />
        </Form.Item>

        <Form.Item
          {...restField}
          label={AppStrings.PAM_DYNAMIC_BONUS_TRIGGER_EVENT}
          name={[name, "event_key"]}
          rules={[{ required: true, message: AppStrings.PAM_DYNAMIC_BONUS_TRIGGER_EVENT_REQUIRED }]}
          extra={
            isLegacyUnmapped
              ? AppStrings.PAM_SEGMENTS_LEGACY_TRIGGER_HINT
              : undefined
          }
        >
          <Select
            placeholder={
              eventsLoading
                ? AppStrings.PAM_DYNAMIC_BONUS_LOADING_TRIGGER_EVENTS
                : AppStrings.PAM_SEGMENTS_TRIGGER_EVENT_PLACEHOLDER
            }
            options={eventSelectOptions}
            showSearch
            disabled={eventsLoading || eventsError}
          />
        </Form.Item>

        <Form.List name={[name, "field_checks"]}>
          {(checkFields, { add: addCheck, remove: removeCheck }) => (
            <div className={styles.conditionsList}>
              {checkFields.map((checkField) => (
                <FieldCheckRow
                  key={checkField.key}
                  namePath={fieldChecksPath}
                  field={checkField}
                  fields={fields}
                  onRemove={() => removeCheck(checkField.name)}
                />
              ))}
              <Button
                intent="neutral"
                variant="ghost"
                iconLeft={<PlusOutlined />}
                onClick={() => addCheck({ field: "", operator: undefined, value: "" })}
              >
                {AppStrings.PAM_SEGMENTS_ADD_FIELD_CHECK}
              </Button>
            </div>
          )}
        </Form.List>
      </div>
    </div>
  );
};

type Props = {
  /** `Form.List` name for the rules array. Defaults to the top-level `membership_rules` field. */
  name?: (string | number)[];
};

/**
 * Editable membership-rules list for `SegmentDetailPage`'s edit mode.
 * One card per rule (`event_key` + field checks); add/remove rules and
 * field checks follow the same pattern as `ConditionsField`.
 */
const SegmentRulesEditor: React.FC<Props> = ({ name = ["membership_rules"] }) => {
  const {
    data: eventGroups = [],
    isLoading: eventsLoading,
    isError: eventsError,
    refetch: refetchEvents,
  } = useDynamicBonusEvents();
  // Full catalog is valid for segments — CRM dispatches to evaluate_segments
  // (BONENG-915). campaign_start is schedule-injected, so strip it like the
  // campaign wizard picker.
  const eventSelectOptions = useMemo(
    () => toGroupedSelectOptions(withoutCampaignStart(eventGroups)),
    [eventGroups],
  );

  return (
    <Form.List name={name}>
      {(ruleFields, { add, remove }) => (
        <div className={styles.stepsList}>
          {eventsError && (
            <>
              <p className={styles.listError}>
                {AppStrings.PAM_DYNAMIC_BONUS_EVENTS_LOAD_ERROR}
              </p>
              <Button intent="neutral" variant="ghost" onClick={() => refetchEvents()}>
                Retry
              </Button>
            </>
          )}

          {ruleFields.map((field, index) => (
            <RuleCard
              key={field.key}
              namePath={name}
              field={field}
              index={index}
              eventGroups={eventGroups}
              eventSelectOptions={eventSelectOptions}
              eventsLoading={eventsLoading}
              eventsError={eventsError}
              onRemove={() => remove(field.name)}
            />
          ))}

          <Button
            intent="neutral"
            variant="ghost"
            iconLeft={<PlusOutlined />}
            onClick={() => add({ event_key: undefined, field_checks: [] })}
          >
            Add rule
          </Button>
        </div>
      )}
    </Form.List>
  );
};

export default SegmentRulesEditor;
